import { api } from "../../api";
import { useAuthStore } from "../../stores/authStore";
import { useOrgStore } from "../../stores/orgStore";
import type { OrgMembership, OrgRole } from "../../types/contract";

function rememberOrg(org: OrgMembership): void {
  const session = useAuthStore.getState().session;
  if (!session) return;
  const role = (
    org.role === "org_admin" ? "org_admin" : "org_member"
  ) as OrgRole;
  const orgs = [
    ...session.orgs.filter((item) => item.id !== org.org_id),
    { id: org.org_id, role },
  ];
  useAuthStore.setState({ session: { ...session, orgs } });
}

function forgetOrg(orgId: string): void {
  const session = useAuthStore.getState().session;
  if (!session) return;
  const orgs = session.orgs.filter((item) => item.id !== orgId);
  useAuthStore.setState({
    session: { ...session, orgs, role: orgs[0]?.role ?? session.role },
  });
}

// Orchestrates the membership mutations shared by the profile-menu org hub + the no-org gate.
// Each: call the API → adopt the re-issued session (token orgs current) → update the org list from
// what we ALREADY know (so a follow-up list fetch failing can't strand the session) → reconcile
// names/roles in the background. The API call itself is what the caller try/catches for feedback.

export async function joinOrgByCode(joinCode: string): Promise<OrgMembership> {
  const { org } = await api.joinOrg(joinCode);
  rememberOrg(org);
  const rest = useOrgStore
    .getState()
    .orgs.filter((o) => o.org_id !== org.org_id);
  useOrgStore.getState().adopt([...rest, org], org.org_id); // switch into the joined org immediately
  void useOrgStore.getState().refresh(); // best-effort name/role reconcile; failure is harmless
  return org;
}

export async function createOrgAction(name: string): Promise<OrgMembership> {
  const { org } = await api.createOrg(name);
  rememberOrg(org);
  useOrgStore
    .getState()
    .adopt([...useOrgStore.getState().orgs, org], org.org_id);
  void useOrgStore.getState().refresh();
  return org;
}

export async function leaveOrgAction(orgId: string): Promise<void> {
  await api.leaveOrg(orgId);
  forgetOrg(orgId);
  // derive the remaining list from what we hold (names known) — don't depend on a follow-up fetch
  // which if it failed would leave the just-left org active and every scoped request 403-ing.
  const remaining = useOrgStore
    .getState()
    .orgs.filter((o) => o.org_id !== orgId);
  useOrgStore.getState().adopt(remaining); // keeps active if still a member, else another / null → gate
  void useOrgStore.getState().refresh();
}
